<!-- src/views/Socios/components/MembresiaReciboModal.vue -->
<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import html2canvas from 'html2canvas'
import { getEmpresaCached } from '@/views/ConfigEmpresa/services/empresa.api'

const props = defineProps({
    show: { type: Boolean, default: false },
    membresia: { type: Object, default: null },
    socio: { type: Object, default: null },
})

const emit = defineEmits(['close'])

// ── refs ────────────────────────────────────────────────────────────────────
const reciboRef = ref(null)
const descargando = ref(false)
const empresa = ref(null)

async function cargarEmpresa() {
  try {
    empresa.value = await getEmpresaCached()
  } catch { empresa.value = null }
}
onMounted(cargarEmpresa)
watch(() => props.show, (v) => { if (v) cargarEmpresa() })

// ── computed helpers ─────────────────────────────────────────────────────────
const nombreSocio = computed(() => props.socio?.nombre || '—')

const iniciales = computed(() => {
    const n = nombreSocio.value.trim()
    return n
        .split(/\s+/)
        .slice(0, 2)
        .map(p => p[0]?.toUpperCase() || '')
        .join('') || '—'
})

const nombreMembresia = computed(() => props.membresia?.membresia?.nombre || '—')
const fechaInicio = computed(() => formatFecha(props.membresia?.fecha_inicio))
const fechaFin = computed(() => formatFecha(props.membresia?.fecha_fin))
const estado = computed(() => props.membresia?.estado || '—')
const money = (value) => new Intl.NumberFormat('es-EC', { style: 'currency', currency: 'USD' }).format(Number(value ?? 0))
const precioLista = computed(() => money(props.membresia?.precio_lista))
const descuento = computed(() => `${props.membresia?.descuento_porcentaje ?? 0}% (${money(props.membresia?.descuento_monto)})`)
const precio = computed(() => money(props.membresia?.precio_unitario))
const codigoRecibo = computed(() => {
    const id = props.membresia?.id ?? '000'
    const uid = props.socio?.id ?? '000'
    return `MEM-${String(uid).padStart(4, '0')}-${String(id).padStart(5, '0')}`
})
const fechaEmision = computed(() => {
    return new Intl.DateTimeFormat('es-EC', {
        day: '2-digit', month: 'long', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    }).format(new Date())
})

const duracionDias = computed(() => {
    if (!props.membresia?.fecha_inicio || !props.membresia?.fecha_fin) return null
    const inicio = new Date(props.membresia.fecha_inicio)
    const fin = new Date(props.membresia.fecha_fin)
    return Math.round((fin - inicio) / (1000 * 60 * 60 * 24))
})

const estadoColor = computed(() => ({
    activa: '#2dce89',
    pendiente: '#fb6340',
    programada: '#8898aa',
    vencida: '#f5365c',
    cancelada: '#525f7f',
    suspendida: '#ffd600',
}[estado.value] || '#8898aa'))

const saldoPendiente = computed(() => Number(props.membresia?.saldo ?? props.membresia?.saldo_pendiente ?? 0))
const totalPagado = computed(() => Number(props.membresia?.total_abonado ?? props.membresia?.pagado_total ?? 0))
const saldoFormatted = computed(() => money(saldoPendiente.value))
const pagadoFormatted = computed(() => money(totalPagado.value))

function formatFecha(raw) {
    if (!raw) return '—'
    const d = new Date(raw + 'T00:00:00')
    return new Intl.DateTimeFormat('es-EC', {
        day: '2-digit', month: 'short', year: 'numeric'
    }).format(d)
}

// ── Generador de imagen ───────────────────────────────────────────────────────
async function descargarImagen() {
    if (!reciboRef.value) return
    try {
        descargando.value = true
        await nextTick()

        const canvas = await html2canvas(reciboRef.value, {
            scale: 2.5,
            useCORS: true,
            backgroundColor: null,
            logging: false,
        })

        const link = document.createElement('a')
        link.download = `recibo-${codigoRecibo.value}.png`
        link.href = canvas.toDataURL('image/png')
        link.click()
    } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.error('Error al generar imagen:', e)
    } finally {
        descargando.value = false
    }
}

// ── Compartir (Web Share API) ─────────────────────────────────────────────────
const canShare = computed(() => !!navigator.share && !!navigator.canShare)

async function compartir() {
    if (!reciboRef.value) return
    try {
        descargando.value = true
        await nextTick()

        const canvas = await html2canvas(reciboRef.value, {
            scale: 2.5,
            useCORS: true,
            backgroundColor: null,
            logging: false,
        })

        canvas.toBlob(async (blob) => {
            const file = new File([blob], `recibo-${codigoRecibo.value}.png`, { type: 'image/png' })
            if (navigator.canShare({ files: [file] })) {
                await navigator.share({
                    title: `Membresía ${nombreMembresia.value}`,
                    text: `Recibo de membresía de ${nombreSocio.value}`,
                    files: [file],
                })
            }
            descargando.value = false
        }, 'image/png')
    } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.error('Error al compartir:', e)
        descargando.value = false
    }
}

// ── close on ESC ─────────────────────────────────────────────────────────────
let escHandler = null
watch(() => props.show, (v) => {
    if (v) {
        escHandler = (e) => { if (e.key === 'Escape') emit('close') }
        document.addEventListener('keydown', escHandler)
    } else {
        if (escHandler) {
            document.removeEventListener('keydown', escHandler)
            escHandler = null
        }
    }
})
onBeforeUnmount(() => {
    if (escHandler) document.removeEventListener('keydown', escHandler)
})
</script>

<template>
    <!-- Backdrop -->
    <Teleport to="body">
        <Transition name="fade">
            <div v-if="show" class="recibo-backdrop" @click.self="$emit('close')">
                <div class="recibo-dialog">

                    <!-- Barra superior del modal -->
                    <div class="recibo-modal-header">
                        <span class="recibo-modal-title">
                            <i class="ni ni-badge me-2"></i>
                            Recibo de Membresía
                        </span>
                        <button class="recibo-close-btn" @click="$emit('close')">
                            <i class="ni ni-fat-remove"></i>
                        </button>
                    </div>

                    <!-- ── TARJETA (lo que se captura) ─────────────────── -->
                    <div ref="reciboRef" class="membership-card">

                        <!-- Header con gradiente - dinámico desde /config/empresa -->
                        <div class="card-header-band">
                            <div class="gym-brand">
                                <img v-if="empresa?.logo_url || empresa?.logo" :src="empresa.logo_url || empresa.logo" class="gym-logo" alt="Logo" />
                                <img v-else src="/imagenes/logogym.png" class="gym-logo" alt="Logo" />
                                <div>
                                    <div class="gym-name">{{ empresa?.nombre || empresa?.nombre_empresa || 'Vendetta Fitness' }}</div>
                                    <div class="gym-tagline">{{ empresa?.slogan || 'teVaAdolerPeroTeVaAgustar' }}</div>
                                    <div v-if="empresa?.ruc" class="gym-ruc">RUC: {{ empresa.ruc }}</div>
                                </div>
                            </div>
                            <div class="card-type-badge">MEMBRESÍA</div>
                        </div>

                        <!-- Estado destacado -->
                        <div class="status-strip" :style="{ background: estadoColor }">
                            <i class="fas fa-check-circle me-1" v-if="estado === 'activa'"></i>
                            <i class="fas fa-clock me-1" v-else-if="estado === 'pendiente'"></i>
                            <i class="fas fa-ban me-1" v-else></i>
                            {{ estado.toUpperCase() }}
                        </div>

                        <!-- Cuerpo principal -->
                        <div class="card-body-main">

                            <!-- Lado izquierdo: avatar + socio -->
                            <div class="member-section">
                                <div class="member-avatar">{{ iniciales }}</div>
                                <div class="member-info">
                                    <div class="member-name">{{ nombreSocio }}</div>
                                    <div class="member-meta">ID: {{ socio?.id || '—' }}</div>
                                    <div class="member-meta">{{ socio?.email || '—' }}</div>
                                    <div class="member-meta">{{ socio?.telefono || '—' }}</div>
                                </div>
                            </div>

                            <!-- Separador vertical -->
                            <div class="vertical-divider"></div>

                            <!-- Lado derecho: datos de membresía -->
                            <div class="membresia-section">
                                <div class="plan-name">{{ nombreMembresia }}</div>

                                <div class="info-grid">
                                    <div class="info-item">
                                        <span class="info-label">Inicio</span>
                                        <span class="info-value">{{ fechaInicio }}</span>
                                    </div>
                                    <div class="info-item">
                                        <span class="info-label">Vencimiento</span>
                                        <span class="info-value">{{ fechaFin }}</span>
                                    </div>
                                    <div class="info-item" v-if="duracionDias">
                                        <span class="info-label">Duración</span>
                                        <span class="info-value">{{ duracionDias }} días</span>
                                    </div>
                                    <div class="info-item">
                                        <span class="info-label">Precio Lista</span>
                                        <span class="info-value">{{ precioLista }}</span>
                                    </div>
                                    <div class="info-item">
                                        <span class="info-label">Descuento</span>
                                        <span class="info-value">{{ descuento }}</span>
                                    </div>
                                    <div class="info-item">
                                        <span class="info-label">Precio Final</span>
                                        <span class="info-value price-value">{{ precio }}</span>
                                    </div>
                                    <div class="info-item" v-if="totalPagado > 0">
                                        <span class="info-label">Abonado / Pagado</span>
                                        <span class="info-value text-success fw-bold">{{ pagadoFormatted }}</span>
                                    </div>
                                    <div class="info-item" v-if="saldoPendiente > 0">
                                        <span class="info-label">Saldo Pendiente</span>
                                        <span class="info-value text-danger fw-bold">{{ saldoFormatted }}</span>
                                    </div>
                                    <div class="info-item">
                                        <span class="info-label">Estado Pago</span>
                                        <span class="info-value">
                                            <span class="badge px-2 py-1" :class="{
                                                'bg-success': saldoPendiente === 0,
                                                'bg-warning text-dark': saldoPendiente > 0 && totalPagado > 0,
                                                'bg-danger': totalPagado === 0 && saldoPendiente > 0
                                            }" style="font-size: 0.65rem; letter-spacing: 0.5px;">
                                                {{ saldoPendiente === 0 ? 'PAGADO' : (totalPagado > 0 ? 'ABONO PARCIAL' : 'PENDIENTE') }}
                                            </span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Footer del recibo -->
                        <div class="card-footer-band">
                            <div class="codigo-recibo">
                                <span class="codigo-label">Código:</span>
                                <span class="codigo-value">{{ codigoRecibo }}</span>
                            </div>
                            <div class="emision-date">Emitido: {{ fechaEmision }}</div>
                        </div>

                        <!-- Marca de agua decorativa - dinámica -->
                        <div class="watermark">{{ (empresa?.nombre || 'fitgestión').toLowerCase() }}</div>
                        <div v-if="empresa?.ruc || empresa?.representante" class="card-footer-sub">
                          <small v-if="empresa?.ruc">RUC: {{ empresa.ruc }}</small>
                          <small v-if="empresa?.ruc && empresa?.representante"> • </small>
                          <small v-if="empresa?.representante">{{ empresa.representante }}</small>
                          <small v-if="empresa?.direccion"> — {{ empresa.direccion }}</small>
                        </div>
                    </div>

                    <!-- Botones de acción -->
                    <div class="recibo-actions">
                        <button class="btn-action btn-download" @click="descargarImagen" :disabled="descargando">
                            <i class="ni ni-cloud-download-95 me-1"></i>
                            {{ descargando ? 'Generando…' : 'Descargar imagen' }}
                        </button>
                        <button v-if="canShare" class="btn-action btn-share" @click="compartir" :disabled="descargando">
                            <i class="ni ni-send me-1"></i>
                            Compartir
                        </button>
                        <button class="btn-action btn-close-action" @click="$emit('close')">
                            Cerrar
                        </button>
                    </div>

                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Caveat:wght@700&display=swap');

/* ── Backdrop / Dialog ──────────────────────────────────────────────────────── */
.recibo-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(3px);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
}

.recibo-dialog {
    background: #f4f6fb;
    border-radius: 16px;
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.3);
    width: 100%;
    max-width: 680px;
    overflow: hidden;
}

/* ── Modal header ───────────────────────────────────────────────────────────── */
.recibo-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.85rem 1.25rem;
    background: #fff;
    border-bottom: 1px solid #e9ecef;
}

.recibo-modal-title {
    font-weight: 700;
    font-size: 0.95rem;
    color: #344767;
}

.recibo-close-btn {
    background: none;
    border: none;
    font-size: 1.2rem;
    cursor: pointer;
    color: #8898aa;
    line-height: 1;
    padding: 0;
    transition: color 0.15s;
}

.recibo-close-btn:hover {
    color: #344767;
}

/* ── Tarjeta principal ──────────────────────────────────────────────────────── */
.membership-card {
    position: relative;
    margin: 1.25rem;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 8px 28px rgba(52, 71, 103, 0.15);
    background: #ffffff;
    font-family: 'Open Sans', 'Segoe UI', sans-serif;
}

/* ── Header gradiente ───────────────────────────────────────────────────────── */
.card-header-band {
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
    padding: 1.1rem 1.4rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.gym-brand {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}



.gym-logo {
    height: 7.7rem;
    width: auto;
    object-fit: contain;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.4));
}

.gym-name {
    font-size: 1.4rem;
    font-weight: 800;
    color: #ffffff;
    letter-spacing: 1.5px;
    text-transform: uppercase;
}

.gym-tagline {
    font-family: 'Caveat', 'Comic Sans MS', cursive, sans-serif;
    font-size: 1.2rem;
    font-weight: 700;
    color: #ffffff;
    letter-spacing: 0.5px;
    text-transform: none;
}
.gym-ruc {
    font-size: 0.65rem;
    color: rgba(255,255,255,0.75);
    letter-spacing: 0.5px;
}
.card-footer-sub {
    text-align: center;
    font-size: 0.65rem;
    color: #8898aa;
    padding: 0.4rem 1rem 0.6rem;
    border-top: 1px solid #f0f2f5;
}

.card-type-badge {
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.25);
    color: #fff;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 2px;
    padding: 0.3rem 0.8rem;
    border-radius: 20px;
    text-transform: uppercase;
}

/* ── Status strip ───────────────────────────────────────────────────────────── */
.status-strip {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    padding: 0.35rem 1rem;
    color: #fff;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 2.5px;
    text-transform: uppercase;
}

/* ── Cuerpo ─────────────────────────────────────────────────────────────────── */
.card-body-main {
    display: flex;
    gap: 0;
    padding: 1.2rem 1.4rem;
    align-items: stretch;
}

/* Sección miembro */
.member-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    min-width: 140px;
    padding-right: 1.2rem;
    text-align: center;
}

.member-avatar {
    width: 68px;
    height: 68px;
    border-radius: 50%;
    background: linear-gradient(135deg, #0f3460, #533483);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.4rem;
    font-weight: 700;
    color: #fff;
    letter-spacing: 1px;
    box-shadow: 0 4px 14px rgba(15, 52, 96, 0.3);
    flex-shrink: 0;
}

.member-name {
    font-size: 0.9rem;
    font-weight: 700;
    color: #344767;
    line-height: 1.2;
}

.member-meta {
    font-size: 0.72rem;
    color: #8898aa;
    line-height: 1.5;
}

/* Separador vertical */
.vertical-divider {
    width: 1px;
    background: linear-gradient(to bottom, transparent, #dee2e6, transparent);
    margin: 0 1.2rem;
    flex-shrink: 0;
}

/* Sección membresía */
.membresia-section {
    flex: 1;
}

.plan-name {
    font-size: 1.1rem;
    font-weight: 800;
    color: #0f3460;
    margin-bottom: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.6rem 1rem;
}

.info-item {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
}

.info-label {
    font-size: 0.65rem;
    font-weight: 700;
    color: #8898aa;
    text-transform: uppercase;
    letter-spacing: 0.8px;
}

.info-value {
    font-size: 0.85rem;
    font-weight: 600;
    color: #344767;
}

.price-value {
    font-size: 1rem;
    font-weight: 800;
    color: #2dce89;
}

/* ── Footer ─────────────────────────────────────────────────────────────────── */
.card-footer-band {
    background: #f8f9fa;
    border-top: 1px dashed #dee2e6;
    padding: 0.65rem 1.4rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.codigo-recibo {
    display: flex;
    align-items: center;
    gap: 0.4rem;
}

.codigo-label {
    font-size: 0.65rem;
    font-weight: 700;
    color: #8898aa;
    text-transform: uppercase;
    letter-spacing: 0.8px;
}

.codigo-value {
    font-size: 0.78rem;
    font-weight: 700;
    color: #344767;
    font-family: 'Courier New', monospace;
    background: #e9ecef;
    padding: 0.15rem 0.5rem;
    border-radius: 4px;
}

.emision-date {
    font-size: 0.67rem;
    color: #adb5bd;
}

/* ── Marca de agua ──────────────────────────────────────────────────────────── */
.watermark {
    position: absolute;
    bottom: 28px;
    right: -22px;
    font-size: 4.5rem;
    font-weight: 900;
    color: rgba(15, 52, 96, 0.04);
    letter-spacing: 6px;
    text-transform: uppercase;
    transform: rotate(-20deg);
    pointer-events: none;
    user-select: none;
}

/* ── Botones de acción ──────────────────────────────────────────────────────── */
.recibo-actions {
    display: flex;
    gap: 0.65rem;
    padding: 0.85rem 1.25rem;
    background: #fff;
    border-top: 1px solid #e9ecef;
    justify-content: flex-end;
    flex-wrap: wrap;
}

.btn-action {
    border: none;
    border-radius: 8px;
    padding: 0.5rem 1.1rem;
    font-size: 0.82rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
}

.btn-action:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.btn-download {
    background: linear-gradient(135deg, #0f3460, #533483);
    color: #fff;
    box-shadow: 0 4px 12px rgba(15, 52, 96, 0.25);
}

.btn-download:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(15, 52, 96, 0.35);
}

.btn-share {
    background: linear-gradient(135deg, #2dce89, #1bae72);
    color: #fff;
    box-shadow: 0 4px 12px rgba(45, 206, 137, 0.3);
}

.btn-share:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(45, 206, 137, 0.4);
}

.btn-close-action {
    background: #f4f6fb;
    color: #8898aa;
    border: 1px solid #dee2e6;
}

.btn-close-action:hover {
    background: #e9ecef;
    color: #344767;
}

/* ── Transición fade ────────────────────────────────────────────────────────── */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
